'use client';

import type { Category } from '@prezly/sdk';
import { FormattedMessage } from '@prezly/theme-kit-nextjs';
import classNames from 'classnames';
import Link from 'next/link';
import { usePathname } from 'next/navigation';

import { useLocale } from '@/adapters/client';
import { translations } from '@/custom/i18n';

import styles from './CategoryFilter.module.scss';

interface Props {
    categories: Category[];
    selectedCategoryId?: number | null;
    parentCategory?: 'news' | 'blogs';
}

export function CategoryFilter({ categories, selectedCategoryId, parentCategory }: Props) {
    const locale = useLocale();
    const pathname = usePathname();
    const isHomePage = pathname === `/${locale}`;
    const isNewsPage = pathname === `/${locale}/news`;
    const isBlogsPage = pathname === `/${locale}/blogs`;

    const parentPath = parentCategory || (pathname.includes('/news') ? 'news' : 'blogs');
    const isParentPage =
        (parentPath === 'news' && isNewsPage) || (parentPath === 'blogs' && isBlogsPage);

    return (
        <div className={styles.container}>
            <Link
                href={`/${locale}/${parentPath}`}
                className={classNames(styles.button, {
                    [styles.active]: !selectedCategoryId && (isParentPage || !isHomePage),
                })}
            >
                <FormattedMessage for={translations.custom.all} locale={locale} />
            </Link>
            {categories.map((category) => {
                const categoryName = (category.i18n[locale]?.name || category.display_name)
                    .split(' / ')
                    .pop();
                const categoryUrl = `/${locale}/category/${category.i18n[locale]?.slug}`;

                return (
                    <Link
                        key={category.id}
                        href={categoryUrl}
                        className={classNames(styles.button, {
                            [styles.active]: category.id === selectedCategoryId,
                        })}
                    >
                        {categoryName}
                    </Link>
                );
            })}
        </div>
    );
}
